import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AUTH_REDIRECT_DELAY_MS, AuthErrorFallback } from '@components/auth-error-fallback';
import { AuthLoadingSkeleton } from '@components/auth-loading-skeleton';
import { REDIRECT_URL, SELECT_VENUE_ROUTE } from '@constants/routes';
import { FeatureFlagContext } from '@contexts/featureFlag/featureFlag-context';
import { UserContext } from '@contexts/user/user-context';
import { useWebviewEvents } from '@contexts/webview-bridge';
import { useChangeUser } from '@hooks/auth/use-change-user';
import { useSignin } from '@hooks/auth/use-signin';
import { useTokenManagement } from '@hooks/auth/use-token-management';
import { useGetUser } from '@hooks/user/user-get-user';
import { usePostHog } from '@posthog/react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { setCurrencyPreferences } from '@utils/price-formatter/price-formatter';

import { AuthContext } from './auth-context';

// Dev auto-login credentials (only set in .env.dev for localhost development)
const DEV_EMAIL = import.meta.env.VITE_EMAIL as string | undefined;
const DEV_PASSWORD = import.meta.env.VITE_PASSWORD as string | undefined;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { getTokens, setTokens, clearTokens } = useTokenManagement();
  const { sendLogout } = useWebviewEvents();
  const posthog = usePostHog();

  const { isPublicEventReportPage } = useContext(FeatureFlagContext);
  const { setUserState, setIsUserLoading } = useContext(UserContext);
  // hideError: auto-login failures are handled gracefully (fallback to signOut + redirect),
  // so we suppress the default error toast to avoid noisy UX on stale dev credentials.
  const { mutateAsync: signinAsync } = useSignin({ hideError: true });
  const autoLoginAttemptedRef = useRef(false);

  // Get current tokens to determine authentication state
  const tokens = getTokens();

  // User is authenticated if they have a valid access token
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!tokens.accessToken;
  });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const redirectUrlRef = useRef('');
  const isErrorRedirectRef = useRef(false);
  const [impersonateUser, setImpersonateUser] = useState<{ email: string; uuid: string }>({ email: '', uuid: '' });

  const { data: impersonateCredentials } = useChangeUser({
    uuid: impersonateUser.uuid,
    options: { enabled: !!impersonateUser.uuid && !isPublicEventReportPage },
  });

  // Enable user query if we have any token (access or refresh)
  // The refresh flow will handle token renewal automatically
  const hasAnyToken = !!(tokens.accessToken ?? tokens.refreshToken);

  const {
    data: user,
    isLoading: isUserLoading,
    isError,
  } = useGetUser({
    enabled: hasAnyToken && !isPublicEventReportPage,
    retry: 2,
  });

  const isImpersonated =
    !!getTokens({ isImpersonate: true }).accessToken || !!getTokens({ isImpersonate: true }).refreshToken;

  const signOut = useCallback(() => {
    const currentUrl = globalThis.location.href;
    // Fire-and-forget bridge notification so the xceedPro Android shell can
    // clear its own session in parallel. `isErrorRedirectRef.current` is set
    // by error-path callers (attemptAutoLoginOrSignOut, recoverSessionOrSignOut)
    // before signOut runs, so it correctly distinguishes the two flows.
    sendLogout({ reason: isErrorRedirectRef.current ? 'Auth error' : 'User initiated' }).catch((): void => undefined);
    posthog?.reset();
    clearTokens();
    queryClient.clear();
    redirectUrlRef.current = `${REDIRECT_URL}=${currentUrl}`;
    setIsRedirecting(true);
  }, [clearTokens, posthog, queryClient, sendLogout]);

  /**
   * Attempt dev auto-login using .env credentials.
   * Only runs once per session (guarded by autoLoginAttemptedRef).
   * On success: stores tokens and reloads the page.
   * On failure: falls back to signOut.
   */
  const attemptAutoLoginOrSignOut = useCallback(
    async ({ isErrorFlow }: { isErrorFlow: boolean }) => {
      const fallbackSignOut = () => {
        if (isErrorFlow) isErrorRedirectRef.current = true;
        signOut();
      };

      // If an auto-login attempt is already in flight or has finished, do nothing
      // so we don't cancel the pending signin.
      if (autoLoginAttemptedRef.current) return;

      // No dev credentials: fall back to the normal signOut + redirect flow.
      if (!DEV_EMAIL || !DEV_PASSWORD) {
        fallbackSignOut();
        return;
      }

      autoLoginAttemptedRef.current = true;
      try {
        const authResponse = await signinAsync({ email: DEV_EMAIL, password: DEV_PASSWORD });
        setTokens({ accessToken: authResponse.accessToken, refreshToken: authResponse.refreshToken });
        globalThis.location.reload();
      } catch {
        fallbackSignOut();
      }
    },
    [signinAsync, setTokens, signOut]
  );

  const recoverSessionOrSignOut = useCallback(
    () => attemptAutoLoginOrSignOut({ isErrorFlow: true }),
    [attemptAutoLoginOrSignOut]
  );

  const changeUser = useCallback(
    ({ email, uuid }: { email: string; uuid: string }) => {
      const { accessToken, refreshToken } = getTokens();
      if (accessToken && refreshToken) {
        setTokens({ accessToken, refreshToken, isImpersonate: true });
        setImpersonateUser({ email, uuid });
      }
    },
    [getTokens, setTokens]
  );

  const resetAdminUser = useCallback(() => {
    const { refreshToken: adminRefreshToken, accessToken: adminAccessToken } = getTokens({ isImpersonate: true });
    if (adminAccessToken || adminRefreshToken) {
      setTokens({ accessToken: adminAccessToken ?? undefined, refreshToken: adminRefreshToken ?? '' });
    }
    clearTokens({ isImpersonate: true });
    globalThis.location.reload();
  }, [getTokens, setTokens, clearTokens]);

  useEffect(() => {
    if (impersonateCredentials) {
      setTokens({
        accessToken: impersonateCredentials.accessToken,
        refreshToken: impersonateCredentials.refreshToken,
      });
      globalThis.open(SELECT_VENUE_ROUTE, '_self');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [impersonateCredentials]);

  useEffect(() => {
    if (isPublicEventReportPage) setIsUserLoading(false);
    else setIsUserLoading(isUserLoading);
    if (user) {
      Sentry.setUser({ email: user.email, id: user.id });
      // Impersonated sessions stay anonymous so admin activity doesn't count toward customer DAU.
      // posthog persists identification across sessions; re-identifying the same user on every
      // page load triggers a redundant feature-flag reload, so only identify on an actual change.
      if (!isImpersonated && posthog && posthog.get_distinct_id() !== user.id) {
        posthog.identify(user.id, { email: user.email });
      }
      setUserState(user);
      i18n.changeLanguage(user.lang);
      setCurrencyPreferences(user.currencyFormat, user.currencyDisplay, user.lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, i18n, isUserLoading, isPublicEventReportPage]);

  useEffect(() => {
    if (isPublicEventReportPage) {
      setIsAuthenticated(true);
      return;
    }

    // Only sign out if there's no refresh token (can't recover)
    const currentTokens = getTokens();
    if (!currentTokens.refreshToken) {
      attemptAutoLoginOrSignOut({ isErrorFlow: false });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPublicEventReportPage]);

  // If user query fails after all retries, attempt dev auto-login or sign out
  useEffect(() => {
    if (!isError || isPublicEventReportPage) return;
    attemptAutoLoginOrSignOut({ isErrorFlow: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isPublicEventReportPage]);

  // Redirect to login when signing out
  // Error redirects wait AUTH_REDIRECT_DELAY_MS so the user sees the error page;
  // voluntary logouts redirect immediately.
  useEffect(() => {
    if (!isRedirecting) return;

    const loginUrl = redirectUrlRef.current || `${REDIRECT_URL}=${globalThis.location.href}`;
    const delay = isErrorRedirectRef.current ? AUTH_REDIRECT_DELAY_MS : 0;
    const timer = setTimeout(() => {
      globalThis.location.href = loginUrl;
    }, delay);

    return () => clearTimeout(timer);
  }, [isRedirecting]);

  // Repo rule, and Sonar S6481: a fresh object here re-renders every consumer on every
  // render of this provider, which wraps the whole app. Pre-existing, and fixed in this
  // PR because the split made it cheap to verify — touching this file now selects one
  // test file instead of 222.
  const value = useMemo(
    () => ({ isAuthenticated, signOut, recoverSessionOrSignOut, changeUser, resetAdminUser, isImpersonated }),
    [isAuthenticated, signOut, recoverSessionOrSignOut, changeUser, resetAdminUser, isImpersonated]
  );

  return (
    <AuthContext.Provider value={value}>
      {isRedirecting ? (
        <AuthLoadingSkeleton>{isErrorRedirectRef.current ? <AuthErrorFallback /> : null}</AuthLoadingSkeleton>
      ) : isPublicEventReportPage || user ? (
        children
      ) : (
        <AuthLoadingSkeleton />
      )}
    </AuthContext.Provider>
  );
};
